// CellModal — React port of Backup/static/js/components/modals.js. Provides a context-mounted
// modal (the #cellModal markup from base.html) plus useCellModal().showCellModal(title, content)
// for expanding truncated table cells. Closes on outside-click and Escape.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { CellModalContext } from './cellModalContext';
import './modal.css';

interface CellModalState {
  title: string;
  content: ReactNode;
}

export function CellModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CellModalState | null>(null);

  const showCellModal = useCallback((title: string, content: ReactNode) => {
    // Validate inputs (matches modals.js: ignore empty content).
    if (!title || content == null || content === '') return;
    if (typeof content === 'string' && content.trim() === '') return;
    setState({ title, content });
  }, []);

  const close = useCallback(() => setState(null), []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [state, close]);

  return (
    <CellModalContext.Provider value={{ showCellModal }}>
      {children}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click-to-dismiss is a pointer-only convenience; keyboard users dismiss via Escape. */}
      <div
        className="cell-modal"
        id="cellModal"
        style={{ display: state ? 'block' : 'none' }}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <div className="cell-modal-content">
          <div className="cell-modal-header">
            <h3 className="cell-modal-title" id="cellModalTitle">
              {state?.title ?? 'Cell Content'}
            </h3>
          </div>
          <div className="cell-modal-body" id="cellModalContent">
            {state?.content ?? ''}
          </div>
        </div>
      </div>
    </CellModalContext.Provider>
  );
}
